import { useState, type FormEvent } from "react";
import { ArrowUpRight, Plus } from "lucide-react";
import { counterpartStage } from "@/platform/playbooks/counterpart";
import { getPlaybook, PLAYBOOK_LIST } from "@/platform/playbooks/registry";
import { LOOP_STAGES, type PlaybookId } from "@/platform/playbooks/types";
import { go } from "@/shared/dashboard/shell/navigate";
import { useLanguage } from "@/shared/i18n/LanguageContext";
import { playbookCopy } from "@/shared/i18n/playbooks";
import { ToolPageHeader } from "../ToolPage";
import { counterpartsCopy } from "./copy";
import { LoopLine } from "./LoopLine";
import { createCounterpart, useCounterparts } from "./useCounterparts";
import "./counterparts.css";

export function counterpartPath(id: string) {
  return `/counterparts/${encodeURIComponent(id)}`;
}

function NewCounterpartForm({ onCancel }: { onCancel: () => void }) {
  const { locale } = useLanguage();
  const copy = counterpartsCopy(locale);
  const pb = playbookCopy(locale);
  const [name, setName] = useState("");
  const [playbook, setPlaybook] = useState<PlaybookId>(PLAYBOOK_LIST[0]!.id);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    setFailed(false);
    try {
      const created = await createCounterpart(name.trim(), playbook);
      go(counterpartPath(created.id));
    } catch {
      setFailed(true);
      setBusy(false);
    }
  };

  return (
    <form
      className="eq-surface eq-panel eq-counterpart-create"
      onSubmit={submit}
      aria-labelledby="new-counterpart-title"
    >
      <h2 id="new-counterpart-title">{copy.createTitle}</h2>
      <label className="eq-field-label">
        {copy.nameLabel}
        <input
          className="eq-input"
          value={name}
          maxLength={120}
          placeholder={copy.namePlaceholder}
          onChange={(event) => setName(event.target.value)}
          autoFocus
          required
        />
      </label>
      <fieldset className="eq-counterpart-playbooks">
        <legend className="eq-field-label">{copy.playbookLabel}</legend>
        {PLAYBOOK_LIST.map((item) => (
          <label
            key={item.id}
            className={`eq-counterpart-playbook ${playbook === item.id ? "is-selected" : ""}`}
          >
            <input
              type="radio"
              name="playbook"
              value={item.id}
              checked={playbook === item.id}
              onChange={() => setPlaybook(item.id)}
            />
            <span>
              <strong>{pb(item.labels.name)}</strong>
              <small>{pb(item.labels.summary)}</small>
            </span>
          </label>
        ))}
      </fieldset>
      {failed && (
        <p role="alert" className="eq-session-error">
          {copy.createError}
        </p>
      )}
      <div className="eq-counterpart-actions">
        <button
          type="submit"
          className="eq-button eq-button--primary"
          disabled={!name.trim() || busy}
        >
          {busy ? copy.creating : copy.create}
        </button>
        <button type="button" className="eq-button" onClick={onCancel}>
          {copy.cancel}
        </button>
      </div>
    </form>
  );
}

/** Shown before the first counterpart: the four steps ARE the explanation. */
function LoopExplainer() {
  const { locale } = useLanguage();
  const copy = counterpartsCopy(locale);
  return (
    <ol className="eq-loop-explainer">
      {LOOP_STAGES.map((stage, index) => (
        <li key={stage}>
          <span className="eq-loop-explainer-number" aria-hidden="true">
            {index + 1}
          </span>
          <strong>{copy.stages[stage]}</strong>
          <p>{copy.stageBodies[stage]}</p>
        </li>
      ))}
    </ol>
  );
}

export function CounterpartsPage() {
  const { locale } = useLanguage();
  const copy = counterpartsCopy(locale);
  const pb = playbookCopy(locale);
  const { counterparts, loading, error, retry } = useCounterparts();
  const [creating, setCreating] = useState(
    () => new URLSearchParams(window.location.search).get("new") === "1",
  );
  const empty = !loading && !error && counterparts.length === 0;

  return (
    <div className="eq-tools-page eq-counterparts-page">
      <ToolPageHeader
        title={copy.title}
        description={copy.lede}
        actions={
          !creating && !empty ? (
            <button
              className="eq-button eq-button--primary"
              onClick={() => setCreating(true)}
            >
              <Plus size={16} aria-hidden="true" />
              {copy.newCounterpart}
            </button>
          ) : undefined
        }
      />
      {creating && <NewCounterpartForm onCancel={() => setCreating(false)} />}
      {loading && <p role="status">{copy.loading}</p>}
      {error && (
        <p role="alert">
          {copy.loadError}{" "}
          <button className="eq-button" onClick={retry}>
            {copy.retry}
          </button>
        </p>
      )}
      {empty && !creating && (
        <section className="eq-surface eq-panel eq-counterparts-empty">
          <h2>{copy.emptyTitle}</h2>
          <p>{copy.emptyBody}</p>
          <LoopExplainer />
          <button
            className="eq-button eq-button--primary"
            onClick={() => setCreating(true)}
          >
            <Plus size={16} aria-hidden="true" />
            {copy.newCounterpart}
          </button>
        </section>
      )}
      {counterparts.length > 0 && (
        <ul className="eq-counterpart-list">
          {counterparts.map((item) => {
            const stage = counterpartStage(item.file);
            return (
              <li key={item.id}>
                <a
                  href={counterpartPath(item.id)}
                  className="eq-surface eq-surface--interactive eq-counterpart-row"
                  onClick={(event) => {
                    event.preventDefault();
                    go(counterpartPath(item.id));
                  }}
                >
                  <span className="eq-counterpart-row-main">
                    <strong>{item.name}</strong>
                    <small>{pb(getPlaybook(item.file.playbook).labels.name)}</small>
                  </span>
                  <span className="eq-counterpart-row-stage">
                    <LoopLine stage={stage} />
                    <small>
                      {copy.next}: {copy.stages[stage]}
                    </small>
                  </span>
                  <ArrowUpRight size={16} aria-hidden="true" />
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
